import { Portal, Content } from "@radix-ui/react-dialog";
import FundamenteiWithText from "components/branding/FundamenteiWithText";
import { Dialog, DialogOverlay, DialogTrigger } from "components/ui/Dialog";
import classNames from "components/ui/classNames";
import { Provider, atom, useAtom, useAtomValue } from "jotai";
import { TvIcon, XIcon } from "lucide-react";
import type { ReactNode } from "react";

const isKioskingAtom = atom(false);
const useIsKiosking = () => useAtomValue(isKioskingAtom);

export function ToggleKioskModeButton() {
  const isKiosking = useIsKiosking();
  return (
    <DialogTrigger asChild={true}>
      <button
        type="button"
        className={classNames(
          "hidden lg:flex",
          "rounded-xl bg-slate-50/60 p-2",
          "border border-slate-200 text-sm font-medium tracking-wide text-slate-600",
          "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
          {
            "bg-violet-50 text-violet-600 dark:bg-violet-600/20 dark:text-violet-200": isKiosking,
          },
          "focus-visible:outline-none",
          "focus-visible:ring-1 focus-visible:ring-offset-1",
          "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
          "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
          "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
          "hover:bg-slate-100 hover:dark:bg-slate-600",
        )}
      >
        <TvIcon />
      </button>
    </DialogTrigger>
  );
}

interface IsKioskingProps {
  children: (isKiosking: boolean) => ReactNode;
}
export function IsKiosking({ children }: IsKioskingProps) {
  const isKiosking = useAtomValue(isKioskingAtom);
  return <>{children(isKiosking)}</>;
}

interface KioskProps {
  forceOpen?: boolean;
  className?: string;
  children: ReactNode;
  // For when you want the state to be entirely internalized and you don't have a <Provider> outside
  isolated?: boolean;
  theme?: "light" | "dark";
}
export function Kiosk({ theme = "light", className, children, isolated = true, forceOpen = false }: KioskProps) {
  const [isKiosking, setIsKiosking] = useAtom(isKioskingAtom);

  return (
    <Dialog open={isKiosking || forceOpen} onOpenChange={setIsKiosking}>
      {isolated ? <Provider>{children}</Provider> : children}
      <Portal>
        <DialogOverlay
          className={classNames("overflow-y-scroll", {
            dark: theme === "dark",
            "bg-slate-900/70 backdrop-blur-lg": theme === "dark",
          })}
        >
          <div
            className={classNames(
              "fixed inset-0 z-50 flex justify-center",
              {
                dark: theme === "dark",
              },
              className,
            )}
          >
            <Content
              onOpenAutoFocus={(event) => {
                // See: https://github.com/radix-ui/primitives/discussions/935
                event.preventDefault();
              }}
              className={classNames(
                "fixed z-50 flex w-full flex-col rounded-xl p-4 sm:m-12 sm:p-0 sm:shadow-xl",
                "lg:max-w-[calc(100%-theme('spacing.24'))]",
                "focus-visible:outline-none focus-visible:ring-2 dark:focus-visible:ring-slate-950",
                className,
              )}
            >
              <div
                className={classNames(
                  "p-8",
                  "rounded-xl border border-slate-300 bg-white",
                  "dark:border-slate-950 dark:bg-slate-800 dark:shadow-slate-950/60",
                  className,
                )}
              >
                <div className="space-y-6">
                  <header>
                    <FundamenteiWithText
                      fill={theme === "dark" ? "#ffffff" : undefined}
                      className="inline-flex"
                      width={180}
                      height={31}
                    />
                  </header>

                  <button
                    type="button"
                    className={classNames(
                      "absolute right-8 top-2",
                      "rounded-sm opacity-70",
                      "hover:opacity-100",
                      "focus:outline-none focus:ring-2 focus:ring-offset-2",
                      "disabled:pointer-events-none",
                      "rounded-full bg-slate-100 p-2 hover:bg-slate-200/60",
                    )}
                    onClick={() => setIsKiosking((previous) => !previous)}
                  >
                    <XIcon className="h-4 w-4" />
                    <span className="sr-only">Fechar</span>
                  </button>

                  {children}
                </div>
              </div>
            </Content>
          </div>
        </DialogOverlay>
      </Portal>
    </Dialog>
  );
}

interface KioskContextProps {
  children: ReactNode;
}
export function KioskContext({ children }: KioskContextProps) {
  return <Provider>{children}</Provider>;
}
